import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { useGroupSubscriptionConfirm, useTeamSubscriptionsCreate } from '../../../../hooks';
import { ACCESS_ANNUAL, ACCESS_PRO, GROUP_SUBSCRIPTION_TEAM } from '../../../../src/constants';
import { AutocompleteOption } from '../../../../utils/interfaces';
import { Button, CustomSpinner, Divider } from '../../../BaseUI';
import { CheckBoxField, TokenizeTextField } from '../../../FormFields';
import subscriptionEmailsValidation from '../../subscriptionEmailsValidation';
import SettingsHeading from '../SettingsHeading';
import styles from './styles.module.scss';

const validationSchema = () =>
  Yup.object().shape({
    emails: subscriptionEmailsValidation(),
  });

const INITIAL_VALUES = { emails: [], overrideAsPro: false };

export default function AddTeamMembers() {
  const [totalCost, setTotalCost] = useState(0);
  const [totalEligibleSub, setTotalEligibleSubs] = useState(0);

  const { addTeamSubscriptionsAsync } = useTeamSubscriptionsCreate();
  const { mutateAsync: confirmGroupSubscription, isLoading: isLoadingConfirmGroupSubscription } =
    useGroupSubscriptionConfirm();

  const onSubmit = async (values, actions) => {
    const accessLevel = values.overrideAsPro ? ACCESS_PRO : ACCESS_ANNUAL;
    const resp = await addTeamSubscriptionsAsync({
      emails: values.emails.map((email) => email.value),
      access_level: accessLevel,
    });
    if (resp?.success) {
      actions.resetForm();
    }
  };

  const prepareConfirmation = async ({
    emails,
    overrideAsPro,
  }: {
    emails: AutocompleteOption[];
    overrideAsPro: boolean;
  }) => {
    const teamData = await confirmGroupSubscription({
      emails: emails.map((email) => email.value),
      group_subscription_type: GROUP_SUBSCRIPTION_TEAM,
      access_level: overrideAsPro ? ACCESS_PRO : ACCESS_ANNUAL,
    });
    setTotalCost(teamData.totalPrice);
    setTotalEligibleSubs(teamData.totalEligibleSubs);
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={INITIAL_VALUES} validationSchema={validationSchema}>
      {({ isSubmitting, isValid, dirty, values }) => (
        <Form data-test="add-to-team-form">
          <SettingsHeading title="Invite Team Members By Email" />
          <TokenizeTextField
            className="!tw-mt-0"
            name="emails"
            placeholder="Enter emails"
            onChangeCallBack={() => prepareConfirmation(values)}
          />
          <span className={styles.tokenizeHint}>
            Add people by email separating each address by a comma.
          </span>

          {totalEligibleSub > 0 && isValid && dirty && (
            <>
              <Divider marginY="medium" />
              <div className={styles.totalContainer}>
                <div>Accounts total: {totalEligibleSub}</div>
                {isLoadingConfirmGroupSubscription ? (
                  <CustomSpinner color="#d04237" size={10} />
                ) : (
                  <div>${totalCost / 100}.00</div>
                )}
              </div>
              <Divider marginY="medium" />
            </>
          )}
          <div className="tw-flex tw-items-center items">
            <Button variant="primary" className="tw-mt-16" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <CustomSpinner color="#ffffff" size={10} /> : <>Add To Team</>}
            </Button>
            <CheckBoxField
              className="tw-flex tw-items-center tw-ml-8 tw-mt-16"
              name="overrideAsPro"
              onChangeCallBack={() => prepareConfirmation(values)}
            />
            <span className="tw-mt-16 tw-text-body-sm">Add as &ldquo;pro&rdquo; account</span>
          </div>
        </Form>
      )}
    </Formik>
  );
}
